import type { KnowledgeMetaSection } from '@cortex/domain-ac-mens-hub-api';
import { Link } from '@tanstack/react-router';
import { BookOpen, ExternalLink, Search } from 'lucide-react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { Card } from '@cortex/web-design-system/components/ui/Card';
import { useKnowledgeMetaSearch } from '../hooks/useKnowledgeMeta.js';

/**
 * 種類の表示順。収録件数の多い順ではなく、運用判断での使う順に並べる。
 * ヘルプセンターが仕様の正本、Marketing API が一次ソース、変更履歴が差分。
 * @graph-connects none
 */
const SECTION_ORDER: readonly KnowledgeMetaSection[] = [
  '01_ヘルプセンター',
  '02_MarketingAPI',
  '03_変更履歴',
  '04_エンジニアリングブログ',
  '05_ニュースルーム',
  '06_その他公式コンテンツ',
  '07_Metaニュースルーム',
];

/**
 * @graph-node {Page}
 * @graph-stack ac-mens-hub
 * @graph-domain Marketing
 * @graph-business Meta公式情報の検索ページ。Meta が実際に書いている原文をキーワードで引き、ヒット箇所の抜粋つきで一覧する。X などで流通する「公式に存在しない通説」に基づく判断を避けるために使う。
 * @graph-connects useKnowledgeMetaSearch [calls] Meta公式情報の検索
 */
export function KnowledgeMetaPage() {
  const { t } = useTranslation();
  const [input, setInput] = useState('');
  const [keyword, setKeyword] = useState('');
  const [section, setSection] = useState<KnowledgeMetaSection | undefined>(undefined);
  const { data, isLoading, error } = useKnowledgeMetaSearch(keyword, section);

  // React の FormEvent は deprecated 扱いのため、既存画面（KeyIssuesPage）と同じ構造的な型で受ける
  const submit = (e: { preventDefault: () => void }) => {
    e.preventDefault();
    setKeyword(input.trim());
  };

  const countOf = (s: KnowledgeMetaSection): number =>
    data?.sectionCounts.find((c) => c.section === s)?.count ?? 0;

  return (
    <div className="mx-auto w-full max-w-5xl px-4 py-10">
      <div className="mb-6">
        <div className="mb-1 font-mono text-xs tracking-widest text-[var(--color-accent-primary)] uppercase">
          Meta Official
        </div>
        <h1 className="text-2xl font-bold text-[var(--color-text-primary)]">
          {t('knowledgeMeta.title')}
        </h1>
        <p className="mt-1 text-sm text-[var(--color-text-secondary)]">
          {t('knowledgeMeta.description')}
        </p>
      </div>

      <form onSubmit={submit} className="mb-6 flex gap-2">
        <input
          value={input}
          onChange={(e) => {
            setInput(e.target.value);
          }}
          placeholder={t('knowledgeMeta.searchPlaceholder')}
          aria-label={t('knowledgeMeta.searchPlaceholder')}
          className="flex-1 rounded border border-[var(--color-border)] bg-[var(--color-bg-primary)] px-3 py-2 text-sm text-[var(--color-text-primary)]"
        />
        <button
          type="submit"
          className="flex items-center gap-1 rounded bg-[var(--color-accent-primary)] px-4 py-2 text-sm font-semibold text-white"
        >
          <Search className="h-4 w-4" aria-hidden="true" />
          {t('knowledgeMeta.search')}
        </button>
      </form>

      {keyword.length === 0 && (
        <div className="py-16 text-center">
          <BookOpen
            className="mx-auto mb-3 h-10 w-10 text-[var(--color-text-muted)]"
            aria-hidden="true"
          />
          <p className="text-sm text-[var(--color-text-muted)]">{t('knowledgeMeta.emptyPrompt')}</p>
        </div>
      )}

      {isLoading && (
        <div className="py-16 text-center text-sm text-[var(--color-text-muted)]">
          {t('app.loading')}
        </div>
      )}

      {error && (
        <Card className="border-[var(--color-error)]/20 p-6 text-center">
          <p className="text-sm text-[var(--color-error)]">{t('knowledgeMeta.fetchError')}</p>
        </Card>
      )}

      {data && keyword.length > 0 && (
        <div className="grid gap-6 md:grid-cols-[200px_1fr]">
          <aside>
            <h2 className="mb-2 text-xs font-semibold tracking-wider text-[var(--color-text-muted)]">
              {t('knowledgeMeta.filterBySection')}
            </h2>
            <ul className="overflow-hidden rounded border border-[var(--color-border)]">
              <li>
                <button
                  type="button"
                  onClick={() => {
                    setSection(undefined);
                  }}
                  className={`flex w-full justify-between px-3 py-2 text-left text-sm ${section === undefined ? 'bg-[var(--color-bg-tertiary)] font-bold' : ''}`}
                >
                  <span>{t('knowledgeMeta.allSections')}</span>
                  <span className="tabular-nums">{data.total}</span>
                </button>
              </li>
              {SECTION_ORDER.map((s) => (
                <li key={s} className="border-t border-[var(--color-border)]">
                  <button
                    type="button"
                    onClick={() => {
                      setSection(s);
                    }}
                    className={`flex w-full justify-between px-3 py-2 text-left text-sm ${section === s ? 'bg-[var(--color-bg-tertiary)] font-bold' : ''}`}
                  >
                    <span>{t(`knowledgeMeta.section.${s}`)}</span>
                    <span className="tabular-nums">{countOf(s)}</span>
                  </button>
                </li>
              ))}
            </ul>
          </aside>

          <main>
            <p className="mb-3 text-sm text-[var(--color-text-secondary)]">
              {t('knowledgeMeta.resultCount', {
                count: section === undefined ? data.total : countOf(section),
              })}
            </p>

            {data.entries.length === 0 && (
              <div className="py-12 text-center">
                <p className="text-sm text-[var(--color-text-muted)]">
                  {t('knowledgeMeta.noResult', { keyword })}
                </p>
                <p className="mt-2 text-xs text-[var(--color-text-muted)]">
                  {t('knowledgeMeta.noResultHint')}
                </p>
              </div>
            )}

            {data.entries.map((e) => (
              <Card key={e.docId} className="mb-3 p-4">
                <div className="mb-1 flex flex-wrap items-center gap-2 text-xs text-[var(--color-text-muted)]">
                  <span className="rounded bg-[var(--color-bg-tertiary)] px-2 py-0.5 font-semibold">
                    {t(`knowledgeMeta.section.${e.section}`)}
                  </span>
                  {e.sourceUpdatedAt !== null && (
                    <span>
                      {t('knowledgeMeta.metaUpdated', { date: e.sourceUpdatedAt.slice(0, 10) })}
                    </span>
                  )}
                  <span className="tabular-nums">
                    {t('knowledgeMeta.charCount', { count: e.charCount })}
                  </span>
                </div>
                <h3 className="mb-1 text-base font-bold">
                  <Link
                    to="/meta-docs/$docId"
                    params={{ docId: e.docId }}
                    className="text-[var(--color-text-primary)] hover:underline"
                  >
                    {e.title}
                  </Link>
                </h3>
                <p className="mb-2 text-sm leading-relaxed text-[var(--color-text-secondary)]">
                  {e.snippet}
                </p>
                <a
                  href={e.sourceUrl}
                  target="_blank"
                  rel="noreferrer"
                  className="inline-flex items-center gap-1 text-xs break-all text-[var(--color-text-muted)] hover:underline"
                >
                  <ExternalLink className="h-3 w-3 shrink-0" aria-hidden="true" />
                  {t('knowledgeMeta.source')} {e.sourceUrl}
                </a>
              </Card>
            ))}
          </main>
        </div>
      )}
    </div>
  );
}
